import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Mail } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const WHATSAPP_URL = "https://wa.me/33652882160";

export function Wordmark({ size = "h-14" }) {
  return (
    <Link to="/" data-testid="wordmark-link" className="inline-block leading-none">
      <img src="/assets/brand/logo.jpg" alt="The Nice Picnic — Côte d'Azur" className={`${size} w-auto rounded-lg`} />
    </Link>
  );
}

export function LangSwitch({ className = "" }) {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center gap-1 text-xs tracking-[0.2em] font-sans ${className}`} data-testid="lang-switch">
      <button data-testid="lang-switch-fr" onClick={() => setLang("fr")}
        className={`px-1.5 py-1 transition-colors ${lang === "fr" ? "text-terracotta font-semibold" : "text-earth hover:text-ink"}`}>FR</button>
      <span className="text-champagne">|</span>
      <button data-testid="lang-switch-en" onClick={() => setLang("en")}
        className={`px-1.5 py-1 transition-colors ${lang === "en" ? "text-terracotta font-semibold" : "text-earth hover:text-ink"}`}>EN</button>
    </div>
  );
}

function Header() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/experiences/boho-picnic", label: t("nav.boho") },
    { to: "/experiences/floating-picnic", label: t("nav.floating") },
    { to: "/experiences/picnic-electronic", label: t("nav.electronic") },
    { to: "/private-events", label: t("nav.privateEvents") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/about", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-ivory/85 backdrop-blur-md border-b border-champagne/70" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Wordmark />
        <nav className="hidden lg:flex items-center gap-6" aria-label="Main">
          {links.slice(0, 6).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) => `text-[0.8rem] tracking-[0.12em] uppercase transition-colors ${isActive ? "text-terracotta" : "text-ink/80 hover:text-terracotta"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <LangSwitch className="hidden sm:flex" />
          <Link to="/booking" data-testid="header-book-btn"
            className="hidden md:inline-flex items-center bg-terracotta text-ivory text-xs tracking-[0.16em] uppercase px-6 py-3 rounded-full hover:bg-clay transition-colors duration-300">
            {t("nav.book")}
          </Link>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden text-ink p-2" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-champagne px-6 py-6 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="block py-3 font-serif text-xl text-ink border-b border-champagne/60">
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center justify-between pt-5">
            <LangSwitch />
            <Link to="/booking" data-testid="mobile-book-btn" className="bg-terracotta text-ivory text-xs tracking-[0.16em] uppercase px-6 py-3 rounded-full">
              {t("nav.book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useLanguage();
  const nav = [
    { to: "/experiences/boho-picnic", label: t("nav.boho") },
    { to: "/experiences/floating-picnic", label: t("nav.floating") },
    { to: "/experiences/picnic-electronic", label: t("nav.electronic") },
    { to: "/private-events", label: t("nav.privateEvents") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/about", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];
  return (
    <footer data-testid="site-footer" className="bg-ink text-ivory/90">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Wordmark size="h-16" />
          <p className="mt-5 text-sm text-ivory/60 max-w-xs leading-relaxed">{t("footer.tagline")}</p>
          <div className="flex gap-4 mt-6">
            <a data-testid="footer-instagram" href="https://www.instagram.com/the.nicepicnic" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
              <Instagram size={16} />
            </a>
            <a data-testid="footer-facebook" href="https://www.facebook.com/thenicepicnic" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
              className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
              <Facebook size={16} />
            </a>
            <a data-testid="footer-whatsapp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
              className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
              <WhatsAppIcon size={16} />
            </a>
            <a data-testid="footer-email" href="mailto:hello@thenicepicnic.com" aria-label="Email"
              className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors">
              <Mail size={16} />
            </a>
          </div>
        </div>
        <div className="md:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.28em] text-gold mb-5">{t("footer.navigation")}</h4>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
            {nav.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-sm text-ivory/70 hover:text-ivory transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.28em] text-gold mb-5">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm text-ivory/70">
            <li><a href={WHATSAPP_URL} className="hover:text-ivory transition-colors">WhatsApp : +33 6 52 88 21 60</a></li>
            <li><a href="mailto:hello@thenicepicnic.com" className="hover:text-ivory transition-colors">hello@thenicepicnic.com</a></li>
            <li>@the.nicepicnic</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ivory/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-ivory/50">
          <span>{t("footer.rights")}</span>
          <div className="flex gap-6">
            <Link data-testid="footer-privacy" to="/legal/privacy" className="hover:text-ivory transition-colors">{t("footer.privacy")}</Link>
            <Link data-testid="footer-terms" to="/legal/terms" className="hover:text-ivory transition-colors">{t("footer.terms")}</Link>
            <Link data-testid="footer-cookies" to="/legal/cookies" className="hover:text-ivory transition-colors">{t("footer.cookies")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35M12.05 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.82 9.82 0 0 1-1.51-5.26c0-5.44 4.43-9.87 9.89-9.87a9.82 9.82 0 0 1 6.99 2.9 9.82 9.82 0 0 1 2.89 6.98c0 5.45-4.44 9.88-9.88 9.88m8.42-18.29A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.33.16 11.87c0 2.1.55 4.14 1.59 5.94L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45c6.56 0 11.9-5.33 11.9-11.88 0-3.18-1.24-6.16-3.47-8.41"/>
    </svg>
  );
}

function WhatsAppFloat() {
  return (
    <a data-testid="whatsapp-float" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#1faa55] text-ivory flex items-center justify-center shadow-[0_10px_30px_rgba(42,33,29,0.25)] hover:scale-110 transition-transform duration-300">
      <WhatsAppIcon size={26} />
    </a>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
